<template>
    <main class="flex flex-col">
        <header>
            <div class="rounded bg-white p-24 shadow flex items-center justify-between gap-8">
                <search
                    class="w-2/5"
                    :autofocus="false"
                    :placeholder="$t('applications.search')"
                    :value="filter.text_keyword"
                    @input="updateSearch"
                />
                <div>
                    <strong>{{ totalApplications }} {{ $t((totalApplicationCount === 1) ? 'general.result' : 'general.results') }}</strong>
                </div>
            </div>
            <div class="my-24 flex items-center gap-12 flex-wrap">
                <action-button
                    :icon="filter.favorites ? 'star-filled' : 'star'"
                    icon-before
                    icon-color="semantic-warning"
                    @click="filter.favorites = !filter.favorites; applyFilter(false)"
                >
                    {{ $t('general.favorites') }}
                </action-button>

                <action-button
                    icon="add"
                    @click="toggleFilter"
                >
                    {{ $t('applications.add_filter') }}
                </action-button>

                <popup-modal
                    v-if="showFilter"
                    without-padding
                    @close="toggleFilter"
                >
                    <div class="flex w-768">
                        <div class="flex-col w-1/3 flex-none border-r border-primary-20 rounded-l overflow-hidden">
                            <ul class="list-reset">
                                <li v-for="(label, type) in filterTabs"
                                    :key="'filter-' + type"
                                    :class="[getFilterTabClasses(type), label.icon ? 'flex items-center' : '']"
                                    class="flex items-center gap-8 px-16 py-12 cursor-pointer hover:bg-primary hover:text-white hover:border-primary"
                                    @click="setFilterTab(type)"
                                >
                                    <svg-icon v-if="label.icon" :type="label.icon"/>
                                    <span class="first-letter:uppercase">{{ $t(label.title) }}</span>
                                </li>
                            </ul>
                        </div>
                        <div class="flex flex-col w-2/3 px-32 mb-32">
                            <div class="pt-24 mb-32 text-heading-2 font-semibold first-letter:uppercase leading-none text-black">{{ $t('general.filter_on', {subject: $t(filterTabs[filterTabActive].title)}) }}</div>
                            <div class="flex-grow mb-32">
                                <keep-alive>
                                    <component
                                        :is="filterTabActive"
                                        :filter="filter"
                                    >
                                    </component>
                                </keep-alive>
                            </div>

                            <simple-button
                                class="ml-auto"
                                look="primary"
                                display="secondary"
                                icon="replay"
                                @click="applyFilter(true)"
                            >
                                {{ $t('general.apply_filter') }}
                            </simple-button>

                        </div>
                    </div>
                </popup-modal>


                <active-filters
                    ref="activeFilters"
                    :filter="filter"
                    :filterValues="applicationsModuleStore.filterValues"
                    @update="applyFilter(false)"
                />
            </div>
        </header>

        <div class="bg-white rounded shadow">

            <header class="p-24 pb-12 flex items-center gap-12">
                <checkbox
                    :checked="hasCheckedApplications"
                    :indeterminate="!applicationsModuleStore.checkedAllApplications && hasCheckedApplications"
                    :label-class="hasCheckedApplications ? 'text-secondary' : 'text-primary'"
                    @change="handleCheckboxEvent"
                >
                    <template v-if="hasCheckedApplications">
                        {{ $t('general.deselect') + ' ' + checkedApplicationCountText }}
                    </template>
                    <template v-else>
                        {{ $t('general.select_all') }}
                    </template>
                </checkbox>

                <select-dropdown
                    v-if="!inGdprContext"
                    v-model="filter.sort"
                    :disabled="!applicationCount"
                    :options="sortOptions"
                    :optionsClass="'z-10'"
                    :prefix="$t('general.sort_by') + ':'"
                    prefixIcon="swap-vert"
                    class="mr-auto"
                    @change="applyFilter(false, $event)"
                ></select-dropdown>

                <extension-requester
                    v-if="inGdprContext"
                    :disabled="!hasCheckedApplications"
                    :request-filters="applicationsModuleStore.getURLParametersForFilter"
                    :selection="{applications: applicationsModuleStore.checkedApplications}"
                    :total-applications="checkedApplicationCount"
                    :type="applicationsModuleStore.checkedAllApplications && checkedEveryApplicationCount ? 'filter' : 'both'"
                    class="ml-auto"
                    @sent="uncheckAllApplications"
                ></extension-requester>

                <bulk-actions
                    v-if="!inGdprContext"
                    :disabled="!hasCheckedApplications"
                    :request-filters="applicationsModuleStore.getURLParametersForFilter"
                    :selection="{applications: applicationsModuleStore.checkedApplications}"
                    :total-applications="checkedApplicationCount"
                    :type="applicationsModuleStore.checkedAllApplications && checkedEveryApplicationCount ? 'filter' : 'selection'"
                >
                </bulk-actions>
            </header>

            <pagination :data="pagination"
                :on-each-side="2"
                class="p-24 pt-12"
                @paginateTo="getApplications"
            ></pagination>

            <div v-if="applicationsModuleStore.checkedAllApplications && everyApplicationCount > 0"
                 class="bg-primary-5 w-full inline-flex items-center justify-center gap-12 py-12"
            >
                <span
                    v-html="(checkedEveryApplicationCount
                    ? $t('applications.every_candidates_checked', {amount: this.checkedEveryApplicationCount})
                    : $t('applications.checked_candidate_current_page', {amount: this.checkedApplicationCount}))"
                >
                </span>

                <simple-button
                    look="text"
                    @click="checkEveryApplication"
                >
                    <template v-if="checkedEveryApplicationCount">
                        {{ $t('applications.clear_selection') }}
                    </template>
                    <template v-else>
                        {{ $t('applications.every_checked_candidate', {amount: everyApplicationCount}) }}
                    </template>
                </simple-button>
            </div>

            <loading-placeholder v-if="showLoader"
                                 :amount="10"
                                 :show-avatar="true"
                                 size="small"></loading-placeholder>

            <div v-if="hasApplications" id="applications">
                <application
                    v-for="application in applicationsModuleStore.applications"
                    :key="application.id"
                    :application="application"
                    :click-to-route="!hasCheckedApplications"
                    :in-gdpr-context="inGdprContext"
                    :sequence="sequence"
                ></application>
            </div>

            <empty-state
                v-if="!applicationCount && !showLoader"
                :description="$t('applications.empty_state.description')"
                :title="$t('applications.empty_state.title')"
                @click="$refs['activeFilters'].removeAllFilters()"
            />

            <pagination :data="pagination"
                        :on-each-side="2"
                        class="ml-24 mr-16 my-24"
                        @paginateTo="getApplications"
            ></pagination>
        </div>

    </main>
</template>

<script>
import LoadingPlaceholder from '../ui/LoadingPlaceholder.vue';
import EmptyState from '../ui/EmptyState.vue';
import Pagination from '../navigation/Pagination.vue';
import ActiveFilters from '../applications/ActiveFilters.vue';
import Application from '../Application.vue';
import BulkActions from '../ui/BulkActions.vue';
import PopupModal from '../ui/PopupModal.vue';
import SelectDropdown from '../forms/SelectDropdown.vue';
import SimpleButton from '../ui/SimpleButton.vue';
import SvgIcon from '../ui/SvgIcon.vue';
import _clone from 'lodash/clone';
import _isEmpty from 'lodash/isEmpty';
import * as applicationService from '../../services/ApplicationService';
import {normalizeJson} from '../../../shared/helpers';
import ApplicationModel from '../../models/Application';
import Search from '../ui/Search.vue';
import _debounce from 'lodash/debounce';
import ExtensionRequester from '../ui/ExtensionRequester.vue';
import FilterReferral from './FilterReferral.vue';
import IconToggle from '../forms/IconToggle';

//filters
import FilterDate from '../applications/FilterDate.vue';
import FilterJobFunctions from '../applications/FilterJobFunctions.vue';
import FilterScore from '../applications/FilterScore.vue';
import FilterStages from '../applications/FilterStages.vue';
import FilterTags from '../applications/FilterTags.vue';
import FilterOffices from '../applications/FilterOffices';
import FilterUsers from "./FilterUsers.vue";
import ActionButton from "../ui/ActionButton.vue";
import Checkbox from "../forms/Checkbox.vue";
import {mapStores} from "pinia";
import {useRepo} from "pinia-orm";
import {useApplicationsStore} from "../../state/modules/applications";

export default {
    components: {
        Checkbox,
        ActionButton,
        IconToggle,
        LoadingPlaceholder,
        EmptyState,
        Pagination,
        'active-filters': ActiveFilters,
        'application': Application,
        'bulk-actions': BulkActions,
        'filter-date': FilterDate,
        'filter-jobfunctions': FilterJobFunctions,
        'filter-score': FilterScore,
        'filter-stages': FilterStages,
        'filter-tags': FilterTags,
        'filter-referral': FilterReferral,
        'filter-offices': FilterOffices,
        'filter-users': FilterUsers,
        'popup-modal': PopupModal,
        'select-dropdown': SelectDropdown,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'search': Search,
        'extension-requester': ExtensionRequester,
    },
    data: function () {
        return {
            inGdprContext: this.$route.name === 'data-protection',
            searchInput: '',
            filter: Object.assign({
                date_from: '',
                date_to: '',
                jobfunctions: [],
                rejected: [],
                new: false,
                hired: false,
                unrated: -1,
                score: false,
                score_min: 0,
                score_max: 100,
                sort: this.$route.name === 'data-protection' ? 'expires_in' : '-created_at',
                stages: [],
                text_keyword: '',
                tags: [],
                inclusiveTagMode: true,
                offices: [],
                referrals: [],
                favorites: false,
                stale: false,
                interview: false,
                unconfirmedInterview: false,
                user: null,
            }, this.applicationsModuleStore?.filterValues),
            filterTabActive: '',
            filterTabs: {
                'filter-date': {title: 'general.date.title', icon: 'calendar-today'},
                'filter-tags': {title: 'general.tags.title', icon: 'sell'},
                'filter-stages': {title: 'general.stages.title', icon: 'check-circle'},
                'filter-jobfunctions': {title: 'general.functions.title', icon: 'format-list-bulleted'},
                'filter-score': {title: 'general.score.title', icon: 'star'},
                'filter-referral': {title: 'applications.source', icon: 'link'},
                'filter-offices': {title: 'general.offices.title', icon: 'location-city'},
                'filter-users': {title: 'jobs.filters.responsible_users', icon: 'person'}
            },
            showFilter: false,
            sortOptions: [
                {value: '-created_at', label: 'general.sort.date.desc'},
                {value: 'created_at', label: 'general.sort.date.asc'},
                {value: 'score', label: 'general.sort.score.asc'},
                {value: '-score', label: 'general.sort.score.desc'},
                {value: 'first_name', label: 'general.sort.name.asc'},
                {value: '-first_name', label: 'general.sort.name.desc'},
            ],
            totalApplications: 0,
            totalFilteredApplications: 0,
            sendingInvitation: false,
            showLoader: false,
            pagination: {},
            everyApplicationCount: 0,
            checkedEveryApplicationCount: 0,
            countAllApplications: 0,
        };
    },
    computed: {
        ...mapStores(useApplicationsStore),
        applicationCount() {
            return this.applicationsModuleStore.applications.length;
        },
        checkedApplicationCount() {
            return this.applicationsModuleStore.checkedAllApplications
                ? this.totalApplicationCount
                : this.applicationsModuleStore.checkedApplications.length;
        },
        checkedApplicationCountText() {
            return this.checkedEveryApplicationCount ?
                this.checkedEveryApplicationCount : this.checkedApplicationCount + ' ' + this.$t((this.checkedApplicationCount == 1) ? 'general.item' : 'general.items');
        },
        hasApplications() {
            return this.applicationCount >= 1;
        },
        hasCheckedApplications() {
            return this.checkedApplicationCount >= 1;
        },
        hasFilteredApplications() {
            return this.totalFilteredApplications != this.totalApplications;
        },
        totalApplicationCount() {
            return this.totalFilteredApplications;
        },
        totalApplicationCountText() {
            return this.totalApplicationCount + ' ' + this.$t((this.totalApplicationCount == 1) ? 'general.result' : 'general.results');
        },
        sequence() {
            return this.applicationsModuleStore.applications.map(i => i.id);
        },
    },
    created: function () {
        this.applicationsModuleStore.setFilterValues(this.filter);
    },
    methods: {
        checkApplications() {
            //Check all current applications
            this.applicationsModuleStore.checkAllApplications(this.applicationsModuleStore.applications);

            //Check if there are more applications then there are fetched
            if (this.totalApplications > this.applicationCount) {
                //Show notification with possibility to check them all
                this.everyApplicationCount = this.totalApplications;
            }
        },
        checkEveryApplication() {
            if (this.checkedEveryApplicationCount) {
                this.checkedEveryApplicationCount = 0;
                this.everyApplicationCount = 0;
                this.applicationsModuleStore.clearCheckedApplications();
            } else {
                this.checkedEveryApplicationCount = this.everyApplicationCount;
            }
        },
        applyFilter(toggleFilter = false, newSortValue) {
            if(newSortValue) {
                this.filter.sort = newSortValue;
            }

            useApplicationsStore().setFilterValues(this.filter);

            if (toggleFilter) {
                this.toggleFilter();
            }

            this.applicationsModuleStore.resetState();

            this.getApplications(1);
        },
        getFilterTabClasses(tab) {
            if (this.filterTabActive == '') {
                this.filterTabActive = tab;
            }

            let classes = [],
                isLastTab = false,
                keys = Object.keys(this.filterTabs)
            ;

            if (this.filterTabActive == tab) {
                classes.push('bg-primary text-white');
            } else {
                classes.push('text-black');
            }

            for (let i in keys) {
                if ((keys[i] == tab) && (i == (keys.length - 1))) {
                    isLastTab = true;
                    break;
                }
            }

            if (!isLastTab) {
                classes.push('border-b');
                if (this.filterTabActive == tab) {
                    classes.push('border-primary');
                } else {
                    classes.push('border-primary-20');
                }
            }

            return classes;
        },
        currentPage() {
            const urlParams = new URLSearchParams(window.location.search);
            let page = urlParams.get('page');

            if (!page) {
                page = 1;
            }

            return page;
        },
        getApplications(pageNumber = 1) {
            //Set new paginate right away
            if (this.hasApplications && this.pagination.meta) {
                let pageFrom = (this.pagination.meta.per_page * (pageNumber - 1)) + 1;

                this.pagination.meta.current_page = parseInt(pageNumber);
                this.pagination.meta.to = pageFrom + this.pagination.meta.per_page;
                this.pagination.meta.from = pageFrom;
            }

            //Clear current application before loading in the new ones
            this.applicationsModuleStore.clearApplications();

            this.showLoader = true;

            let urlParams = this.applicationsModuleStore.getURLParametersForFilter;

            if (this.inGdprContext) {
                urlParams['filter[where_in_expiration_period]'] = 1;
                urlParams['filter[without_active_data_retention_extension_requests]'] = 1;
            }

            //Get new applications
            applicationService.index({
                sort: this.filter.sort,
                extraParameters: urlParams,
                page: pageNumber,
            }).then(result => {
                this.showLoader = false;
                this.totalApplications = result.meta.total;
                this.totalFilteredApplications = result.data.data.length;

                this.pagination = result;

                return useRepo(ApplicationModel).save(normalizeJson(result.data.data))
            }).then(inserts => {
                if (this.applicationsModuleStore.checkedAllApplications) {
                    this.applicationsModuleStore.checkAllApplications(this.applicationsModuleStore.applications);
                }
            });
        },
        setFilterTab(tab) {
            this.filterTabActive = tab;
        },
        toggleFilter() {
            let visible = !this.showFilter;

            // when modal will be visible:
            // - always toggle first tab
            // - reset filter from state
            if (visible) {
                this.setFilterTab(Object.keys(this.filterTabs)[0]);

                // we may not have a reference between these two variables
                this.filter = _clone(this.applicationsModuleStore.filterValues);
            }

            this.showFilter = visible;
        },
        uncheckAllApplications() {
            this.applicationsModuleStore.clearCheckedApplications();

            this.everyApplicationCount = 0;
            this.checkedEveryApplicationCount = 0;
        },
        updateSearch(input) {
            this.filter.text_keyword = input;
            this.triggerSearch();
        },
        triggerSearch: _debounce(function () {
            this.applyFilter();
        }, 500),
        handleCheckboxEvent() {
            if (this.hasCheckedApplications) {
                this.uncheckAllApplications();
            } else {
                this.checkApplications();
            }
        },
    },
    watch: {
        '$route'() {
            // react to route changes, check if you go back with browser
            if (this.pagination.meta) {
                if (parseInt(this.currentPage()) !== this.pagination.meta.current_page) {
                    this.getApplications(this.currentPage());
                }
            }
        },
    },
    mounted() {
        this.getApplications(this.currentPage());
    },
};
</script>
